import React, { useRef, useEffect } from 'react';
import styled from '@emotion/native';
import { TouchableOpacity, Linking } from 'react-native';
import { ActivityIndicator, Checkbox } from 'react-native-paper';
import { Feather } from '@expo/vector-icons';
import { BodyText } from '@/shared/components/StyledText';
import { FieldTitle } from '@/shared/styles/styledComponents/styledComponents';
import { green, mint, red } from '@/shared/styles/colors';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import { useSignNowDocument } from '@/shared/hooks/useSignNowDocument';
import { EMME_SUPPORT_EMAIL } from '@/shared/constants/utility';

interface SignNowContractSectionProps {
  isStylist?: boolean;
  onContractSigned?: (documentId: string) => void;
}

const SignNowContractSection: React.FC<SignNowContractSectionProps> = ({
  isStylist,
  onContractSigned,
}) => {
  const isDesktop = useIsDesktop();
  const {
    documentId,
    contractLoading,
    contractError,
    isContractSigned,
    handleContractCheckboxPress,
    handleContractLinkPress,
  } = useSignNowDocument(isStylist);

  // Notify parent when contract is signed
  const onContractSignedRef = useRef(onContractSigned);
  onContractSignedRef.current = onContractSigned;

  useEffect(() => {
    if (isContractSigned && documentId && onContractSignedRef.current) {
      onContractSignedRef.current(documentId);
    }
  }, [isContractSigned, documentId]);

  if (!isStylist) {
    return null;
  }

  return (
    <>
      <ContractInstructionsContainer>
        <FieldTitle fontSize={14} marginBottom={10}>
          Sign Stylist Agreement*
        </FieldTitle>
        <ContractStep>
          <ContractStepText>
            <ContractStepNumber>Step 1: </ContractStepNumber>
            Go to{' '}
            <TouchableOpacity
              onPress={handleContractLinkPress}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <ContractLinkText isDesktop={isDesktop}>
                Sign Now
              </ContractLinkText>
              <Feather
                style={{ marginBottom: isDesktop ? 0 : -4 }}
                name="external-link"
                size={14}
                color={green}
              />
            </TouchableOpacity>{' '}
            to sign your Stylist Agreement.
          </ContractStepText>
        </ContractStep>

        <ContractStep>
          <ContractStepText>
            <ContractStepNumber>Step 2: </ContractStepNumber>
            Complete all required fields and sign the agreement.
          </ContractStepText>
        </ContractStep>

        <ContractStep>
          <ContractStepText>
            <ContractStepNumber>Step 3: </ContractStepNumber>
            Return to this page and check the box below to verify completion.
          </ContractStepText>
        </ContractStep>
      </ContractInstructionsContainer>

      <ContractCheckboxContainer
        onPress={handleContractCheckboxPress}
        disabled={contractLoading}
      >
        {contractLoading ? (
          <ContractLoadingWrapper>
            <ActivityIndicator size="small" color={green} />
          </ContractLoadingWrapper>
        ) : (
          <ContractCheckboxWrapper>
            <Checkbox.Android
              status={isContractSigned ? 'checked' : 'unchecked'}
              onPress={handleContractCheckboxPress}
              color={green}
            />
          </ContractCheckboxWrapper>
        )}
        <ContractCheckboxLabel>
          I&apos;ve successfully completed and signed the Stylist Agreement.
        </ContractCheckboxLabel>
      </ContractCheckboxContainer>

      <ContractErrorView>
        {contractError && (
          <ContractErrorText>
            {contractError}
            {'\n'}
            <ContractHelpRow>
              <ContractHelpText>Need help?</ContractHelpText>
              <TouchableOpacity
                onPress={() => Linking.openURL(`mailto:${EMME_SUPPORT_EMAIL}`)}
              >
                <ContractSupportLink>Contact Support</ContractSupportLink>
              </TouchableOpacity>
            </ContractHelpRow>
          </ContractErrorText>
        )}
      </ContractErrorView>
    </>
  );
};

const ContractInstructionsContainer = styled.View({
  marginTop: 10,
});

const ContractStep = styled.View({
  marginBottom: 10,
});

const ContractStepNumber = styled(BodyText)({
  fontWeight: '600',
  fontSize: 12,
});

const ContractStepText = styled(BodyText)({
  flex: 1,
  fontSize: 12,
});

const ContractCheckboxContainer = styled.TouchableOpacity({
  flexDirection: 'row',
  alignItems: 'center',
  padding: 5,
  backgroundColor: mint,
});

const ContractLoadingWrapper = styled.View({
  width: 48,
  height: 24,
  paddingVertical: 18,
  justifyContent: 'center',
  alignItems: 'center',
});

const ContractCheckboxWrapper = styled.View({
  marginRight: 12,
});

const ContractCheckboxLabel = styled(BodyText)({
  flex: 1,
  fontWeight: 'bold',
  fontSize: 12,
  lineHeight: 16,
  color: green,
});

const ContractLinkText = styled(BodyText)<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    color: green,
    textDecorationLine: 'underline',
    fontWeight: 'bold',
    fontSize: 12,
    marginRight: 4,
    lineHeight: 20,
    marginBottom: isDesktop ? 0 : -4,
  }),
);

const ContractErrorView = styled.View({
  paddingVertical: 15,
  width: 'auto',
  marginBottom: 10,
});

const ContractErrorText = styled(BodyText)({
  color: red,
  fontSize: 11,
  lineHeight: 14,
  fontWeight: 'bold',
});

const ContractHelpRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
});

const ContractHelpText = styled(BodyText)({
  fontSize: 11,
  lineHeight: 14,
  color: green,
});

const ContractSupportLink = styled(BodyText)({
  fontSize: 11,
  textDecorationLine: 'underline',
  fontWeight: 'bold',
  paddingLeft: 4,
  color: green,
});

export default SignNowContractSection;
